/**
 * Hook for What's New feature - Version detection and state management
 */

import { useState, useEffect } from "react";
import { useAuthStore } from "~/store/authStore";

const STORAGE_KEY = "odin_whats_new";
const DELAY_MS = 3000; // 3 seconds delay after login

/**
 * Get version from package.json (injected at build time)
 */
const getCurrentVersion = () => {
  return import.meta.env.VITE_APP_VERSION || "1.0.0";
};

/**
 * Get stored What's New preferences from localStorage
 */
const getStoredPreferences = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    return JSON.parse(stored);
  } catch (error) {
    console.error("[WhatsNew] Failed to parse stored preferences:", error);
    return null;
  }
};

/**
 * Save What's New preferences to localStorage
 */
const savePreferences = (preferences) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.error("[WhatsNew] Failed to save preferences:", error);
  }
};

/**
 * Hook for managing What's New panel state
 */
/**
 * Check if What's New should be shown (without side effects)
 * Used by AppRouter to orchestrate modal sequence
 */
/**
 * Mark What's New as seen without opening it
 * Used when ChartSelector takes priority and WhatsNew is skipped
 */
export const markWhatsNewAsSeen = () => {
  const version = getCurrentVersion();
  const preferences = getStoredPreferences() || {};
  savePreferences({
    ...preferences,
    lastSeenVersion: version,
    lastSeenDate: new Date().toISOString(),
  });
  console.log(
    `[WhatsNew] Marked version ${version} as seen (skipped due to ChartSelector)`,
  );
};

export const shouldShowWhatsNew = () => {
  if (sessionStorage.getItem("noPopups") === "true") return false;
  const preferences = getStoredPreferences();
  if (!preferences) return true; // First time user
  return preferences.lastSeenVersion !== getCurrentVersion();
};

export const useWhatsNew = () => {
  const [isOpen, setIsOpen] = useState(false);
  const currentVersion = getCurrentVersion();

  // Listen for AppRouter trigger to open WhatsNew
  useEffect(() => {
    const handler = () => {
      console.log("[WhatsNew] Opened via AppRouter sequence");
      setIsOpen(true);
    };
    window.addEventListener("whatsNew:open", handler);
    return () => window.removeEventListener("whatsNew:open", handler);
  }, []);

  /**
   * Close the panel and mark version as seen
   */
  const close = () => {
    setIsOpen(false);

    // Mark current version as seen
    const preferences = getStoredPreferences() || {};

    savePreferences({
      ...preferences,
      lastSeenVersion: currentVersion,
      lastSeenDate: new Date().toISOString(),
    });

    console.log(`[WhatsNew] Marked version ${currentVersion} as seen`);

    // Notify AppRouter sequence
    window.dispatchEvent(new Event("whatsNew:closed"));
  };

  /**
   * Manually open the panel (from footer button)
   */
  const open = () => {
    setIsOpen(true);
  };

  return {
    isOpen,
    currentVersion,
    open,
    close,
  };
};

export default useWhatsNew;
